import React, { Component } from 'react';
import { Modal, Table, Button } from 'react-bootstrap';

import { FolderClosedIcon, FolderOpenIcon, CaretIcon, iconForFile } from './FileTypeIcons.js';
import VideoPlayerPopup from './VideoPlayerPopup.js';
import YamlEditorPopup from './YamlEditorPopup.js';
import FileViewerPopup from './FileViewerPopup.js';
import RecordingDownloadPopup from './RecordingDownloadPopup.js';
import DesignLibraryPopup from './DesignLibraryPopup.js';
import CloudUpload from './CloudUpload.js';
import ConfirmDeletePopup from '../Controls/ConfirmDeletePopup.js';
import { loadLasHeader } from './lasHeader.js';
import { loadPlyInfo } from './plyHeader.js';

import './RecordingInfo.css';
import '../Controls/MusterTableStyle.css';

//plain-text file types that open in the read-only viewer popup; extension-less files
//(e.g. syslog) are also treated as viewable -- see isViewerFile
export const VIEWER_EXTENSIONS = ['.log', '.timestamps', '.mm', '.pos', '.txt', '.gst-pipeline', '.launch', '.prj', '.proj', '.bash', '.json'];

export const isViewerFile = (lowerName) => {
    return VIEWER_EXTENSIONS.some(ext => lowerName.endsWith(ext)) ||
           lowerName.lastIndexOf('.') <= 0; //no extension (a leading dot alone is not one)
};

//Builds a folder tree from the flat recording_files list, whose filename field is the path
//relative to the recording folder ('bags/p1_0.bag'). Folders only exist implicitly in those
//paths, so they are synthesised here. Each level is sorted folders-first, then files, both
//alphabetical case-insensitive. Folder nodes: { name, path, fileCount, folders, files };
//file nodes keep every servlet field and gain { name, path }.
export const buildTree = (files) => {

    const root = { name: '', path: '', fileCount: 0, folders: [], files: [] };
    //path -> node, so repeated prefixes reuse one node. Prototype-less so folder names like
    //'constructor' cannot collide with Object.prototype members.
    const folderIndex = Object.create(null);

    for (const file of files)
    {
        const segments = file.filename.split('/');
        let node = root;
        let path = '';

        node.fileCount++;

        for (let i = 0; i < segments.length - 1; i++)
        {
            path = path.length > 0 ? path + '/' + segments[i] : segments[i];

            if (!folderIndex[path])
            {
                folderIndex[path] = { name: segments[i], path: path, fileCount: 0, folders: [], files: [] };
                node.folders.push(folderIndex[path]);
            }

            node = folderIndex[path];
            node.fileCount++;
        }

        node.files.push({ ...file, name: segments[segments.length - 1], path: file.filename });
    }

    const byName = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

    const sortNode = (node) => {
        node.folders.sort(byName);
        node.files.sort(byName);
        node.folders.forEach(sortNode);
    };

    sortNode(root);

    return root;
};

class RecordingInfo extends Component {

    constructor(props) {
        super(props);

        this.state = {
            files: [],
            expandedFolders: {},
            originalComment: '',
            recordingComment: '',
            originalName: '',
            recordingName: '',
            showPopup: false,
            showAddFile: false,
            errMsg: ''
        };

        this.handleRecordingInfoChange = this.handleRecordingInfoChange.bind(this);
        this.handleRecordingCommentChange = this.handleRecordingCommentChange.bind(this);

        this.videoPopup = React.createRef();
        this.yamlPopup = React.createRef();
        this.fileViewerPopup = React.createRef();
        this.downloadPopup = React.createRef();
        this.confirmDeletePopup = React.createRef();
        this.designLibraryPopup = React.createRef();
    }

    //header button: zips the whole recording folder on the server and downloads the archive.
    //originalName is the folder on disk -- recordingName is the editable rename field
    handleDownloadZip = () => {

        if (this.downloadPopup.current)
        {
            this.downloadPopup.current.show(this.state.originalName);
        }
    }

    //Engineering designs have a separate, immutable package lifecycle from the
    //ordinary recording files. Keep the package manager scoped to the recording
    //folder selected in this details dialog.
    handleShowDesigns = () => {
        if (this.designLibraryPopup.current)
        {
            this.designLibraryPopup.current.show(this.state.originalName);
        }
    }

    //footer button: opens the shared CloudUpload in an "add files to this recording" mode
    //(originalName, not the editable rename field, is the folder on disk)
    handleAddFile = () => {
        this.setState({ showAddFile: true });
    }

    //closing the upload modal refetches the file list so files just added show in the tree
    handleCloseAddFile = () => {
        this.setState({ showAddFile: false });
        this.refreshFiles();
    }

    //left-click on an mp4 plays it in the popup instead of navigating; the href stays on the
    //anchor so middle-click / save-as still fetch the raw file
    handleVideoClick = (fileUrl) => (event) => {
        event.preventDefault();

        if (this.videoPopup.current)
        {
            this.videoPopup.current.show(fileUrl);
        }
    }

    //left-click on a yaml opens it in the editor popup; href kept, same as mp4s
    handleYamlClick = (filePath) => (event) => {
        event.preventDefault();

        if (this.yamlPopup.current)
        {
            this.yamlPopup.current.show(this.state.originalName, filePath);
        }
    }

    //left-click on a plain-text file opens it in the read-only viewer popup
    handleViewerClick = (filePath) => (event) => {
        event.preventDefault();

        if (this.fileViewerPopup.current)
        {
            this.fileViewerPopup.current.show(this.state.originalName, filePath);
        }
    }

    //left-click on a bag asks the recording_bag_info service to run 'rosbag info' on it and
    //shows the command's output in the read-only viewer popup. The popup opens immediately
    //with a spinner; the token keeps a slow response from touching a closed/reused popup.
    handleBagClick = (filePath) => (event) => {
        event.preventDefault();

        if (!this.fileViewerPopup.current)
        {
            return;
        }

        const token = this.fileViewerPopup.current.showLoading(filePath + ' - rosbag info');

        fetch('recording_bag_info', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: "recording=" + encodeURIComponent(this.state.originalName) +
                  "&filename=" + encodeURIComponent(filePath)
        })
        .then((res) => {
            if (res.ok)
            {
                return res.text();
            }

            throw new Error('HTTP ' + res.status + ' - is the bag info service deployed on this server?');
        })
        .catch(err => 'Could not run rosbag info: ' + err.message)
        .then((text) => {
            if (this.fileViewerPopup.current)
            {
                this.fileViewerPopup.current.populate(token, text);
            }
        })
    }

    //left-click on a ply opens its header plus the size check (header + elements x record
    //bytes vs the size on disk); the tick/cross in the popup corner carries the verdict
    handlePlyClick = (filePath) => (event) => {
        event.preventDefault();

        loadPlyInfo(this.state.originalName, filePath)
        .then((report) => {
            if (this.fileViewerPopup.current)
            {
                this.fileViewerPopup.current.showText(filePath + ' - PLY header', report.text, report.ok ? 'ok' : 'bad');
            }
        })
    }

    //left-click on a las opens its parsed header plus the size check (point data offset +
    //points x record bytes vs the size on disk) in the read-only viewer popup; the
    //tick/cross in the popup corner carries the verdict
    handleLasClick = (filePath) => (event) => {
        event.preventDefault();

        loadLasHeader(this.state.originalName, filePath)
        .then((report) => {
            if (this.fileViewerPopup.current)
            {
                this.fileViewerPopup.current.showText(filePath + ' - LAS header', report.text, report.ok ? 'ok' : 'bad');
            }
        })
    }

    //re-list the recording's files (sizes/dates change after a yaml save or a delete)
    refreshFiles = () => {

        fetch('recording_files', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: "recording=" + this.state.originalName
        })
        .then(res => res.json())
        .then((data) => {
            this.setState({
                files: data
            });
        })
        .catch(console.log)
    }

    show = (filename, comment) => {

        //console.log("show recording file info " + filename + " - " + comment);

        this.setState({
            originalComment: comment,
            recordingComment: comment,
            originalName: filename,
            recordingName: filename,
            expandedFolders: {} //folders start collapsed on every open; survives delete refetches
        });

        fetch('recording_files', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: "recording=" + filename
        })
        .then(res => res.json())
        .then((data) => {
            this.setState({
                showPopup: true,
                files: data
            });
        })
        .catch(console.log)
    }

    handleClose = () => {
        this.setState({ showPopup: false });
        this.props.reloadRecordingsList();
    }

    handleUploadNewFile = () => {
    }

    //delete button entry point: the shared popup prompts first when the
    //'Confirm deletion' setting is on
    handleDeleteRequest = filename => event => {
        event.preventDefault();

        if (this.confirmDeletePopup.current)
        {
            this.confirmDeletePopup.current.request(() => this.doDelete(filename));
        }
    }

    doDelete = (filename) => {

        fetch('recording_file_delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: "recording=" + this.state.originalName + "&filename=" + filename
        })
        .then(res => res.json())
        .then(data =>
        {
            if ('true' === data[0].recordingDeleted)
            {
                this.handleClose();
            }
            else
            {
                if ('true' === data[0].fileDeleted)
                {
                    this.refreshFiles();
                }
            }
        })
        .catch(console.log)
    }

    handleUpdateRecordingName = () => {

        fetch('recording_file_update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: "originalName=" + this.state.originalName + "&newName=" + this.state.recordingName + "&comment=" + this.state.recordingComment
            })
            .then(res => res.json())
            .then((data) => {
                this.handleClose();
            })
            .catch(console.log)
    }

    toggleFolder = (path) => {

        this.setState((state) => {
            const expandedFolders = { ...state.expandedFolders };

            //own-property check ('true ===', not truthiness) so a folder named like an
            //Object.prototype member ('constructor') still toggles correctly
            if (true === expandedFolders[path])
            {
                delete expandedFolders[path];
            }
            else
            {
                expandedFolders[path] = true;
            }

            return { expandedFolders: expandedFolders };
        });
    }

    renderFileRow = (file, depth) => {

        const fileUrl = "maps/" + this.state.recordingName + "/" + file.path;
        const lowerName = file.name.toLowerCase();
        const isMp4 = lowerName.endsWith('.mp4');
        const isYaml = lowerName.endsWith('.yaml') || lowerName.endsWith('.yml');

        let onClick;

        if (isMp4)
        {
            onClick = this.handleVideoClick(fileUrl);
        }
        else if (isYaml)
        {
            onClick = this.handleYamlClick(file.path);
        }
        else if (lowerName.endsWith('.bag'))
        {
            onClick = this.handleBagClick(file.path);
        }
        else if (lowerName.endsWith('.las'))
        {
            onClick = this.handleLasClick(file.path);
        }
        else if (lowerName.endsWith('.ply'))
        {
            onClick = this.handlePlyClick(file.path);
        }
        else if (isViewerFile(lowerName))
        {
            onClick = this.handleViewerClick(file.path);
        }

        return (
        <tr key={file.path}>
            <td className="treeNameCell">
                <span className="treeName" style={{ paddingLeft: (depth * 24) + 'px' }}>
                    {iconForFile(file.name)}
                    <a href={fileUrl} onClick={onClick}>{file.name}</a>
                </span>
            </td>
            <td>
                {file.size}
            </td>
            <td>
                {file.uploadDate}
            </td>
            <td>
                {file.errMsg}
            </td>
            <td>
                {1 === this.props.accessLevel &&
                    <form class="form-inline" name="frmDelete" onSubmit={this.handleDeleteRequest(file.path)}>
                    <Button variant="danger" size="lg" type="submit">Delete</Button>
                    <input type="hidden" name="filename" value={file.path} />
                    </form>
                }
            </td>
        </tr>
        )
    }

    renderFolderRow = (folder, depth, expanded) => (
        <tr key={folder.path} className="folderRow" onClick={() => this.toggleFolder(folder.path)}>
            <td className="treeNameCell">
                <span className="treeName" style={{ paddingLeft: (depth * 24) + 'px' }}>
                    <span className={"treeCaretBox" + (expanded ? " expanded" : "")}><CaretIcon /></span>
                    {expanded ? <FolderOpenIcon /> : <FolderClosedIcon />}
                    <span className="folderName">{folder.name}</span>
                    <span className="folderCount">({folder.fileCount} {1 === folder.fileCount ? "file" : "files"})</span>
                </span>
            </td>
            <td colSpan="3"></td>
            {/* stopPropagation: deleting must not also toggle the folder open/closed */}
            <td onClick={(e) => e.stopPropagation()}>
                {1 === this.props.accessLevel &&
                    <form class="form-inline" name="frmDelete" onSubmit={this.handleDeleteRequest(folder.path)}>
                    <Button variant="danger" size="lg" type="submit">Delete</Button>
                    <input type="hidden" name="filename" value={folder.path} />
                    </form>
                }
            </td>
        </tr>
    )

    //Walks the tree depth-first, folders before files (buildTree pre-sorts each level), and
    //flattens it to table rows. Children of collapsed folders are skipped entirely.
    renderNodeRows = (node, depth, rows) => {

        for (const folder of node.folders)
        {
            const expanded = true === this.state.expandedFolders[folder.path];

            rows.push(this.renderFolderRow(folder, depth, expanded));

            if (expanded)
            {
                this.renderNodeRows(folder, depth + 1, rows);
            }
        }

        for (const file of node.files)
        {
            rows.push(this.renderFileRow(file, depth));
        }

        return rows;
    }

    renderListData = () => {
        return (
            <tbody>
                {this.renderNodeRows(buildTree(this.state.files), 0, [])}
            </tbody>
        )
    }

    handleRecordingInfoChange(event) {

        this.setState({
            recordingName: event.target.value
        });

    }

    handleRecordingCommentChange(event) {

        this.setState({
            recordingComment: event.target.value
        });

    }

    render() {
        return (
            <div>

            <Modal
              dialogClassName="recordingInfoModal"
              show={this.state.showPopup}
              onHide={this.handleClose}
              backdrop="static"
              centered
            >
            <Modal.Header closeButton>
                <Modal.Title><center><h1 class="pageTitle">Recording Info</h1></center></Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Table className="fileTable musterStyleTable">
                <thead>
                    <tr>
                        <th class="recordingFile"><h3>File</h3></th>
                        <th class="recordingFileSize"><h3>File Size</h3></th>
                        <th class="recordingLastUpdated"><h3>Last Updated</h3></th>
                        <th class="recordingError"><h3>Error</h3></th>
                        <th class="recordingFileAbsorbingColumn"></th>
                    </tr>
                </thead>
                {this.renderListData()}
                </Table>
            </Modal.Body>

            <Modal.Footer>
                <Button className="btnDownloadZip" onClick={this.handleDownloadZip}>Download zipped recording</Button>
                <Button className="btnDesignLibrary" onClick={this.handleShowDesigns}>IFC / DXF designs</Button>
                <Button className="btnAddFile" onClick={this.handleAddFile}>Add File</Button>
                <input name="txtRecordingComment" type="text" value={this.state.recordingComment} onChange={this.handleRecordingCommentChange} />
                <input name="txtRecordingName" type="text" value={this.state.recordingName} onChange={this.handleRecordingInfoChange} />
                <Button onClick={this.handleUpdateRecordingName}>Save</Button>
            </Modal.Footer>
            </Modal>

            {/* Upload Map modal, stacked above the Recording Info modal so files can be added
                to this recording; recordingAddFile = the recording folder keeps the upload as a
                subfolder inside it (see CloudUpload) */}
            <Modal
              className="recordingAddFileRoot"
              backdropClassName="recordingAddFileBackdrop"
              dialogClassName="recordingAddFileModal"
              show={this.state.showAddFile}
              onHide={this.handleCloseAddFile}
              centered
            >
            <Modal.Header closeButton>
                <Modal.Title><center><h1 class="pageTitle">Upload Map</h1></center></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <CloudUpload recordingAddFile={this.state.originalName} handleCloseUpload={this.handleCloseAddFile} />
            </Modal.Body>
            <Modal.Footer>
            </Modal.Footer>
            </Modal>

            <VideoPlayerPopup ref={this.videoPopup} />
            <YamlEditorPopup ref={this.yamlPopup} onSaved={this.refreshFiles} />
            <FileViewerPopup ref={this.fileViewerPopup} />
            <RecordingDownloadPopup ref={this.downloadPopup} />
            <DesignLibraryPopup ref={this.designLibraryPopup} />
            <ConfirmDeletePopup ref={this.confirmDeletePopup} />

            </div>
        )
    }
}

export default RecordingInfo;
